import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../assets/icons/close.svg";
import APIVariables from "../../../services/api-variables";
import { ReactComponent as Undo } from "../../../assets/icons/undo.svg";
import { handleCatch, validateEmail } from "../../../shared/helpers";
import { App } from "../../../routes/Constants";
import QuitModal from "../../common/Modal/QuitModal";
import ConfirmConnectedPharma from "./modals/ConfirmConnectedPharmaModal";
import useRoles from "../../../hooks/useRoles";
import Axios from "../../../services/Axios";

const inputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none disabled:cursor-not-allowed";

const requiredInputStyles = `${inputStyles} required-label`;

const ConnectedPharmacistAdd = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quitModalIsOpen, setIsOpenQuit] = useState(false);
  const [confirmModalIsOpen, setConfirmModal] = useState(false);
  const [validateScreenFields, setValidateScreenFields] = useState({});
  const { storageData } = useRoles();
  const [form, setForm] = useState({
    pharmacist: {
      first_name: "",
      last_name: "",
      registration_number: "",
      job_title: "",
      email: "",
    },
  });

  useEffect(() => {
    setValidateScreenFields(validateForm());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ pharmacist: { ...form.pharmacist, [name]: value } });
  };

  function closeForm() {
    setIsOpenQuit(true);
  }

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.CONNECTED_PHARMACISTS);
  }

  const submitForm = async () => {
    setIsSubmitting(false);
    let areAllFields = Object.values(validateScreenFields).every((field) => {
      return field.isValid;
    });

    if (areAllFields) {
      await Axios.post(APIVariables.HCP_CON_PHARMACIST_ADD, form.pharmacist)
        .then((res) => {
          const { status } = res;
          if (status === 201) {
            setConfirmModal(true);
          }
        })
        .catch(function (error) {
          handleCatch(error);
          let serverErrors = error?.response?.data;
          if (!!serverErrors) {
            let currentValidation = validateScreenFields;
            Object.keys(serverErrors)?.forEach((key) => {
              currentValidation[key].isValid = false;
              currentValidation[key].serverError = serverErrors[key]?.join(" ");
            });
            setIsSubmitting(true);
            setValidateScreenFields(currentValidation);
          }
        });
    } else {
      setIsSubmitting(true);
    }
  };

  const validateForm = () => {
    const { first_name, last_name, registration_number, job_title, email } =
      form.pharmacist;
    return {
      first_name: {
        isValid: !!first_name.length,
      },
      last_name: {
        isValid: !!last_name.length,
      },
      registration_number: {
        isValid: !!registration_number.length,
      },
      job_title: {
        isValid: !!job_title.length,
      },
      email: {
        isValid: validateEmail(email),
      },
    };
  };

  const noChangesOnForm = () => {
    return Object.values(form?.pharmacist).every((item) => item.length === 0);
  };

  return (
    <div
      className="min-h-screen overflow-y-auto  pt-[140px] pb-[100px]"
      data-testid="connected-pharmacists-add-page"
    >
      <div className="relative mx-auto max-w-[1100px] rounded-[10px] bg-white px-6 py-5 shadow-[0px_0px_40px_rgba(0,0,0,0.2)] sm:px-10">
        <button
          type="button"
          className="absolute right-[15px] top-[15px] cursor-pointer rounded-full bg-[#9ABDB8] p-[3px]"
          onClick={closeForm}
          aria-label="Close form"
          data-testid="connected-pharmacists-add-close-button"
        >
          <img src={CloseIcon} alt="" className="brightness-0 invert" />
        </button>
        <div className="font-size-18 blue-label">Step 1/1</div>
        <h1 className="select-category-txt mineShaft-color mt-2.5 pb-1.5">
          Connected Pharmacist Registration Form
        </h1>
        <hr className="my-0 h-px border-0 bg-[#768692]" />

        <h2 className="title-details mineShaft-color mt-[30px] mb-[25px]">
          Pharmacist Details
        </h2>

        <div className="mb-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label
                className={`blue-label ${isSubmitting && !validateScreenFields.first_name?.isValid
                    ? "required-label"
                    : ""
                  }`}
                htmlFor="cp-first-name"
              >
                Pharmacist First Name:*
              </label>
              <input
                id="cp-first-name"
                className={
                  isSubmitting && !validateScreenFields.first_name?.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
                type="text"
                onChange={handleChange}
                name="first_name"
                value={form.pharmacist?.first_name || ""}
                maxLength={30}
                data-testid="connected-pharmacists-add-first-name-input"
              />
              {isSubmitting && !validateScreenFields.first_name?.isValid && (
                <span className="required-text">
                  {validateScreenFields.first_name?.serverError || "Required"}
                </span>
              )}
            </div>
            <div>
              <label
                className={`blue-label ${isSubmitting && !validateScreenFields.last_name?.isValid
                    ? "required-label"
                    : ""
                  }`}
                htmlFor="cp-last-name"
              >
                Pharmacist Last Name:*
              </label>
              <input
                id="cp-last-name"
                className={
                  isSubmitting && !validateScreenFields.last_name?.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
                type="text"
                onChange={handleChange}
                name="last_name"
                value={form.pharmacist?.last_name || ""}
                maxLength={30}
                data-testid="connected-pharmacists-add-last-name-input"
              />
              {isSubmitting && !validateScreenFields.last_name?.isValid && (
                <span className="required-text">
                  {validateScreenFields.last_name?.serverError || "Required"}
                </span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label
                className={`blue-label ${isSubmitting && !validateScreenFields.registration_number?.isValid
                    ? "required-label"
                    : ""
                  }`}
                htmlFor="cp-reg"
              >
                GPhC / PSNI Registration Number:*
              </label>
              <input
                id="cp-reg"
                className={
                  isSubmitting && !validateScreenFields.registration_number?.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
                type="text"
                onChange={handleChange}
                name="registration_number"
                value={form.pharmacist?.registration_number || ""}
                maxLength={30}
                data-testid="connected-pharmacists-add-registration-input"
              />
              {isSubmitting && !validateScreenFields.registration_number?.isValid && (
                <span className="required-text">
                  {validateScreenFields.registration_number?.serverError || "Required"}
                </span>
              )}
            </div>
            <div>
              <label
                className={`blue-label ${isSubmitting && !validateScreenFields.job_title?.isValid
                    ? "required-label"
                    : ""
                  }`}
                htmlFor="cp-job-title"
              >
                Job Title:*
              </label>
              <input
                id="cp-job-title"
                className={
                  isSubmitting && !validateScreenFields.job_title?.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
                type="text"
                name="job_title"
                value={form.pharmacist?.job_title || ""}
                onChange={handleChange}
                maxLength={30}
                data-testid="connected-pharmacists-add-job-title-input"
              />
              {isSubmitting && !validateScreenFields.job_title?.isValid && (
                <span className="required-text">
                  {validateScreenFields.job_title?.serverError || "Required"}
                </span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="cp-pharmacy-name">
                Pharmacy Name:
              </label>
              <input
                id="cp-pharmacy-name"
                type="text"
                name="email"
                maxLength={50}
                value={storageData?.pharmacy?.value || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-add-pharmacy-name-input"
              />
            </div>
            <div>
              <label
                className={`blue-label ${isSubmitting && !validateScreenFields.email?.isValid
                    ? "required-label"
                    : ""
                  }`}
                htmlFor="cp-email"
              >
                Email Address: (A link will be sent to verify your email address)*
              </label>
              <input
                id="cp-email"
                className={
                  isSubmitting && !validateScreenFields.email?.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
                type="text"
                onChange={handleChange}
                name="email"
                value={form.pharmacist?.email || ""}
                maxLength={50}
                data-testid="connected-pharmacists-add-email-input"
              />
              {isSubmitting && !validateScreenFields.email?.isValid && (
                <span className="required-text">
                  {validateScreenFields.email?.serverError || "Required"}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="blue-label" htmlFor="cp-pharmacy-address">
              Pharmacy address:
            </label>
            <input
              id="cp-pharmacy-address"
              type="text"
              maxLength={50}
              value={storageData?.pharmacy?.address || ""}
              disabled
              className={inputStyles}
              data-testid="connected-pharmacists-add-pharmacy-address-input"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 pt-[60px] sm:flex-row sm:items-center sm:justify-between">
          <p
            className="text-base blue-label"
            data-testid="connected-pharmacists-add-mandatory-field-note"
          >
            *symbol indicating a mandatory field
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              className="flex items-center gap-1 border-0 bg-transparent font-size-18 text-[#00567a] hover:text-[#8c8c8c] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={noChangesOnForm()}
              onClick={() =>
                setForm({
                  pharmacist: {
                    first_name: "",
                    last_name: "",
                    registration_number: "",
                    job_title: "",
                    email: "",
                  },
                })
              }
              data-testid="connected-pharmacists-add-undo-button"
            >
              <Undo /> Undo Changes
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={submitForm}
              data-testid="connected-pharmacists-add-save-button"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description="Are you sure you want to cancel filling out this form? All progress will be lost and you will need to start again."
        />
      )}
      {confirmModalIsOpen && (
        <ConfirmConnectedPharma
          closeModal={setConfirmModal}
          modalIsOpen={confirmModalIsOpen}
        />
      )}
    </div>
  );
};
export default ConnectedPharmacistAdd;
